import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { logCriticalError } from '@/lib/errorLogger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Compact variant for per-section error boundaries (e.g. admin pages) */
  compact?: boolean;
  /** Called when "Retry" is clicked in compact mode instead of reloading the page */
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/** Stale-deploy / chunk-load errors that aren't actionable bugs. */
const IGNORABLE_RENDER_PATTERNS = [
  /can't find variable/i,
  /importing a module script failed/i,
  /failed to fetch dynamically imported module/i,
  /loading chunk/i,
  /chunkloaderror/i,
  /useglobalaudioplayer must be used within/i,
  /removechild/i,
  /minified react error #321/i,
];

function isIgnorableRenderError(message: string): boolean {
  return IGNORABLE_RENDER_PATTERNS.some((p) => p.test(message || ''));
}



export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Skip non-actionable render errors caused by stale deploys / chunk swaps —
    // these aren't real bugs, they just mean the user had an old tab open.
    if (isIgnorableRenderError(error.message)) {
      return;
    }

    // Log to backend error tracking
    logCriticalError({
      errorCode: 'REACT_RENDER_ERROR',
      errorMessage: error.message,
      errorSource: 'unknown',
      metadata: {
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      },
    });
  }


  handleReload = () => {
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Compact variant for per-section error boundaries
      if (this.props.compact) {
        return (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <AlertTriangle className="h-10 w-10 text-yellow-500 mb-4" />
            <h2 className="text-lg font-semibold text-foreground mb-1">This section encountered an error</h2>
            <p className="text-sm text-muted-foreground mb-4 text-center max-w-md">
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <Button onClick={this.handleRetry} variant="outline" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </div>
        );
      }

      return (
        <div className="min-h-screen gradient-dark flex items-center justify-center p-4">
          <div className="max-w-md text-center space-y-4">
            <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto" />
            <h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
            <p className="text-muted-foreground">
              We've logged this error and are working to fix it.
            </p>
            <Button onClick={this.handleReload} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Reload Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
